import { ApolloError } from "@apollo/client";
import { Alert, Button, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { MultiSelectElement, RadioButtonGroup, SwitchElement } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router";

import { GqlExportTemplate, GqlProjectExportCreateInput, GqlProjectStatus } from "~/__generated__";
import GaiaConfirmCloseDialog from "~/modules/app/components/GaiaConfirmCloseDialog";
import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";
import useRequireCurrentClient from "~/modules/client/hooks/useRequireCurrentClient";

import ProjectSelect from "../ProjectSelect";
import { useProjectExportCreate_GetData } from "./hooks/useProjectExportCreate_GetData";

export type ProjectExportCreateFormInput = Omit<GqlProjectExportCreateInput, "clientId">;

interface ProjectExportCreateFormProps {
    defaultValues?: Partial<ProjectExportCreateFormInput>;
    onSubmit: SubmitHandler<ProjectExportCreateFormInput>;
    error?: ApolloError;
    isProjectsSelectable?: boolean;
}

export function ProjectExportCreateForm({ defaultValues, onSubmit, error, isProjectsSelectable }: ProjectExportCreateFormProps) {
    const { t } = useTranslation("projectExport");
    const { client } = useRequireCurrentClient();
    const [selectedProjectIds, setSelectedProjectIds] = useState(defaultValues?.projectIds ?? []);

    const { handleSubmit, setError, watch, formState, control, setValue, getValues } = useForm<ProjectExportCreateFormInput>({
        defaultValues: {
            template: GqlExportTemplate.Default,
            mediaIncluded: false,
            ...defaultValues,
        },
    });

    const blocker = useBlocker(!formState.isSubmitting && !formState.isSubmitted && formState.isDirty);

    const { templates, researchTypes } = useProjectExportCreate_GetData({
        projectIds: selectedProjectIds,
    });

    const handleSelectAllResearchTypes = () => {
        setValue(
            "researchTypeIds",
            researchTypes.map(x => x.value),
            { shouldDirty: true },
        );
    };

    const handleProjectsClose = () => {
        const current = getValues("projectIds") ?? [];
        const hasChanged = current.length !== selectedProjectIds.length || current.some(id => !selectedProjectIds.includes(id));

        if (hasChanged) setSelectedProjectIds(current);
    };

    const handleOnSubmit: SubmitHandler<ProjectExportCreateFormInput> = values => {
        onSubmit(values);
    };

    const watchedProjectIds = watch("projectIds");
    useApplyErrorToForm(error, setError);

    const isResearchTypeSelectionDisabled = watchedProjectIds === undefined || watchedProjectIds.length === 0;

    return (
        <form onSubmit={handleSubmit(handleOnSubmit)}>
            <GaiaConfirmCloseDialog blocker={blocker} />
            <Stack gap={2}>
                {formState.errors.root && (
                    <Alert severity="error">
                        <Typography>{formState.errors.root.message}</Typography>
                    </Alert>
                )}

                <Controller
                    control={control}
                    disabled={!isProjectsSelectable}
                    rules={{ required: true }}
                    name="projectIds"
                    render={({ field }) => (
                        <ProjectSelect
                            allowSelectAll={isProjectsSelectable}
                            clientIds={[client.id]}
                            disabled={field.disabled}
                            statuses={[GqlProjectStatus.Confirmed, GqlProjectStatus.Completed]}
                            label={t("ProjectExportCreateForm.projectIds")}
                            multiple
                            onClose={handleProjectsClose}
                            onChange={field.onChange}
                            value={field.value}
                        />
                    )}
                />

                <MultiSelectElement
                    control={control}
                    label={t("ProjectExportCreateForm.researchTypes")}
                    rules={{ required: true }}
                    helperText={
                        isResearchTypeSelectionDisabled ? (
                            <Typography variant="caption" color="text.secondary">
                                {t("ProjectExportCreateForm.disabledResearchTypeSelectionHelper")}
                            </Typography>
                        ) : (
                            <Button onClick={handleSelectAllResearchTypes} variant="text" size="small">
                                {t("ProjectExportCreateForm.selectAll")}
                            </Button>
                        )
                    }
                    name="researchTypeIds"
                    itemKey="value"
                    options={researchTypes}
                    showChips
                    disabled={isResearchTypeSelectionDisabled}
                />

                <RadioButtonGroup
                    control={control}
                    label={t("ProjectExportCreateForm.template")}
                    rules={{ required: true }}
                    name="template"
                    options={templates}
                    valueKey="value"
                />

                <SwitchElement control={control} label={t("ProjectExportCreateForm.mediaIncluded")} name="mediaIncluded" />

                <Button
                    sx={{ mt: 3 }}
                    type="submit"
                    color="primary"
                    variant="contained"
                    disabled={!formState.isDirty}
                    loading={formState.isLoading}
                >
                    {t("ProjectExportCreateForm.createButton")}
                </Button>
            </Stack>
        </form>
    );
}
